import { useMutation, useQuery } from '@apollo/client';

import {
    faArrowRightToBracket,
    faArrowRotateRight,
    faCircleCheck,
    faCircleXmark,
    faClock,
    faCloudArrowDown,
    faExternalLink,
    faPencil,
    faPlug,
    faPlus,
    faSpinnerThird,
    faTrashCan,
} from '@fortawesome/pro-regular-svg-icons';
import classnames from '../../../core/utils/classnames.tsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { withIcon } from '../../../core/components/Icon.tsx';
import {
    CHECK_SAGE100_INTEGRATION_CONNECTION,
    DELETE_SAGE100_INTEGRATION,
    GET_SAGE100_IMPORT_PROGRESS,
    GET_SAGE100_INTEGRATIONS,
    INTEGRATIONS_SAGE100_OAUTH2_COMPLETE,
    TRIGGER_SAGE100_INTEGRATION_MASTERDATA_UPDATE,
    TRIGGER_SAGE100_OAUTH_FLOW,
} from '../../queries.ts';
import {
    CUSTOMER_INTEGRATION_ERP_PATH,
    CUSTOMER_INTEGRATION_SAGE100_ADD_PATH,
    CUSTOMER_INTEGRATION_SAGE100_EDIT_PATH,
    CUSTOMER_INTEGRATION_SAGE100_PATH,
} from '../../constants.ts';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { redirectTo, url } from '../../../core/utils/link.ts';
import Modal from '../../../core/components/Modal.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { Outlet } from 'react-router';
import Layout from '../../../core/components/Layout.tsx';
import Page from '../../../core/components/Page/index.tsx';
import Table, { RowControlIconButton } from '../../../core/components/Table.tsx';
import { useOauthCompleteMutation } from '../../utils.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';
import Tooltip from '../../../core/components/Tooltip.tsx';

interface Sage100FormModalOutletContext {
    integrations: any[];
    refetch: () => Promise<any>;
}

const AddIcon = withIcon(faPlus);
const EditIcon = withIcon(faPencil);
const DeleteIcon = withIcon(faTrashCan);
const RefreshIcon = withIcon(faArrowRotateRight);
const TriggerUpdateIcon = withIcon(faCloudArrowDown);
const ExternalLinkIcon = withIcon(faExternalLink);
const LoadingIcon = withIcon(faSpinnerThird);
const LoginIcon = withIcon(faArrowRightToBracket);
const ConnectionIcon = withIcon(faPlug);
const DoneIcon = withIcon(faCircleCheck);
const FailedIcon = withIcon(faCircleXmark);
const QueuedIcon = withIcon(faClock);

interface ImportProgress {
    recordType: string;
    state: 'queued' | 'running' | 'done' | 'failed';
    read: number;
    total: number | null;
    processed: number;
    lastProcessed: number | null;
    progress: number;
    error: string | null;
}

const STATE_ORDER: ImportProgress['state'][] = ['failed', 'running', 'queued', 'done'];

const ImportProgressStateIcon = ({ state }: { state: ImportProgress['state'] }) => {
    if (state === 'done') return <DoneIcon className="text-content-success-strong" />;
    if (state === 'failed') return <FailedIcon className="text-content-danger-strong" />;
    if (state === 'running') return <LoadingIcon spin className="text-content-brand-strong" />;
    return <QueuedIcon className="text-content-medium" />;
};

const ProgressBar = ({ value, failed }: { value: number; failed: boolean }) => (
    <div className="h-1.5 w-full max-w-md rounded-full bg-surface-strong overflow-hidden">
        <div
            className={classnames(
                'h-full rounded-full transition-all duration-300',
                failed ? 'bg-surface-danger-strong' : 'bg-surface-brand-strong'
            )}
            style={{ width: `${Math.round(value * 100)}%` }}
        />
    </div>
);

const groupErrors = (importProgress: ImportProgress[]) => {
    const recordTypesByError = new Map<string, string[]>();
    importProgress
        .filter((entry) => entry.state === 'failed' && entry.error)
        .forEach((entry) => {
            recordTypesByError.set(entry.error!, [...(recordTypesByError.get(entry.error!) || []), entry.recordType]);
        });
    return [...recordTypesByError.entries()];
};

const MasterDataStatus = ({ integration, importProgress }: { integration: any; importProgress: ImportProgress[] }) => {
    const { t, i18n } = useTranslation('integrations');
    const formatNumber = (value: number) => value.toLocaleString(i18n.language);
    const recordTypeLabel = (recordType: string) =>
        t(`sage100.importProgress.recordTypes.${recordType}`, { defaultValue: recordType });
    const updatedAt = integration.masterDataUpdatedAt
        ? new Date(integration.masterDataUpdatedAt).toLocaleString(i18n.language, {
              dateStyle: 'medium',
              timeStyle: 'short',
          })
        : null;

    if (importProgress.length === 0) {
        if (integration.masterDataUpdateActive) {
            return (
                <span className="flex gap-2 items-center">
                    <LoadingIcon spin className="text-content-brand-strong" />
                    {`${t('sage100.masterDataUpdateInProgress')} ${Math.round(integration.fetchProgress * 100)}%`}
                </span>
            );
        }
        if (integration.masterDataUpdateError) {
            return (
                <div className="flex flex-col gap-1">
                    <span className="text-content-danger-strong">{t('sage100.masterDataUpdateError')}</span>
                    <span className="text-content-medium text-sm whitespace-pre-line">
                        {integration.masterDataUpdateError}
                    </span>
                </div>
            );
        }
        return (
            <span className="text-content-medium">
                {updatedAt ? t('sage100.masterData.updatedAt', { date: updatedAt }) : t('sage100.masterData.never')}
            </span>
        );
    }

    const active = integration.masterDataUpdateActive;
    const total = importProgress.length;
    const failed = importProgress.filter((entry) => entry.state === 'failed').length;
    const finished = importProgress.filter((entry) => entry.state === 'done' || entry.state === 'failed').length;
    const records = importProgress.reduce((sum, entry) => sum + (entry.state === 'done' ? entry.processed : 0), 0);
    const overall = importProgress.reduce((sum, entry) => sum + entry.progress, 0) / total;
    const hasError = failed > 0 || (!active && !!integration.masterDataUpdateError);
    const errors = groupErrors(importProgress);
    const sorted = [...importProgress].sort((a, b) => STATE_ORDER.indexOf(a.state) - STATE_ORDER.indexOf(b.state));

    const describe = (entry: ImportProgress) => {
        if (entry.state === 'failed') return t('sage100.importProgress.failed');
        if (entry.state === 'done') return formatNumber(entry.processed);
        if (entry.state === 'queued')
            return active ? t('sage100.importProgress.queued') : t('sage100.masterData.notRun');
        if (entry.total) return `${formatNumber(entry.read)} / ${formatNumber(entry.total)}`;
        if (entry.lastProcessed) return `${formatNumber(entry.processed)} / ~${formatNumber(entry.lastProcessed)}`;
        return formatNumber(entry.processed);
    };

    return (
        <div className="flex flex-col gap-2 py-1">
            <div className="flex flex-col gap-1.5">
                <div className="flex gap-2 items-center flex-wrap">
                    {active ? (
                        <LoadingIcon spin className="text-content-brand-strong" />
                    ) : hasError ? (
                        <FailedIcon className="text-content-danger-strong" />
                    ) : (
                        <DoneIcon className="text-content-success-strong" />
                    )}
                    <span className={classnames('font-medium', hasError && !active && 'text-content-danger-strong')}>
                        {active
                            ? t('sage100.masterData.updating', { finished, total })
                            : hasError
                              ? t('sage100.masterData.failedSummary', { failed: failed || total, total })
                              : t('sage100.importProgress.records', {
                                    count: records,
                                    formatted: formatNumber(records),
                                })}
                    </span>
                    {active && <span className="text-content-medium tabular-nums">{Math.round(overall * 100)}%</span>}
                    {!active && updatedAt && (
                        <span className="text-content-medium">
                            · {t('sage100.masterData.updatedAt', { date: updatedAt })}
                        </span>
                    )}
                </div>
                {active && <ProgressBar value={overall} failed={failed > 0} />}
            </div>

            <ul className="grid gap-x-6 gap-y-1 text-sm grid-cols-[repeat(auto-fill,minmax(14rem,1fr))]">
                {sorted.map((entry) => (
                    <li key={entry.recordType} className="flex gap-2 items-center min-w-0">
                        <span className="w-4 shrink-0">
                            <ImportProgressStateIcon state={entry.state} />
                        </span>
                        <span className="text-content-strong truncate">{recordTypeLabel(entry.recordType)}</span>
                        <span
                            className={classnames(
                                'ml-auto tabular-nums whitespace-nowrap',
                                entry.state === 'failed' ? 'text-content-danger-strong' : 'text-content-medium'
                            )}
                        >
                            {describe(entry)}
                        </span>
                    </li>
                ))}
            </ul>

            {(errors.length > 0 || (hasError && !active && integration.masterDataUpdateError)) && (
                <div className="flex flex-col gap-1 rounded bg-surface-danger-weak px-2 py-1.5 text-sm text-content-danger-strong">
                    {errors.length > 0 ? (
                        errors.map(([error, recordTypes]) => (
                            <span key={error} className="whitespace-pre-line">
                                <span className="font-medium">{recordTypes.map(recordTypeLabel).join(', ')}:</span>{' '}
                                {error}
                            </span>
                        ))
                    ) : (
                        <span className="whitespace-pre-line">{integration.masterDataUpdateError}</span>
                    )}
                </div>
            )}
        </div>
    );
};

const Sage100Row = ({
    integration,
    importProgress,
    refetch,
    onRefreshStatus,
    refreshing,
    loadedAt,
}: {
    integration: any;
    importProgress: ImportProgress[];
    refetch: any;
    onRefreshStatus: () => void;
    refreshing: boolean;
    loadedAt: Date | null;
}) => {
    const { t, i18n } = useTranslation('integrations');
    const { publishToast } = useToaster();

    const [deleteIntegration] = useMutation(DELETE_SAGE100_INTEGRATION, {
        refetchQueries: [GET_SAGE100_INTEGRATIONS],
    });
    const handleDeleteIntegration = async () => {
        try {
            await deleteIntegration({ variables: { id: integration.id } });
            await refetch();
            publishToast({ description: t('sage100.deleteSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('sage100.deleteError'), status: 'error' });
        }
    };

    const [updateMasterDataIntegration] = useMutation(TRIGGER_SAGE100_INTEGRATION_MASTERDATA_UPDATE);
    const handleUpdateMasterDataIntegration = async () => {
        try {
            await updateMasterDataIntegration({ variables: { id: integration.id } });
            await refetch();
            publishToast({ description: t('sage100.updateMasterDataSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('sage100.updateMasterDataError'), status: 'error' });
        }
    };

    const [checkConnection, { loading: checkingConnection }] = useMutation(CHECK_SAGE100_INTEGRATION_CONNECTION);
    const [connectionState, setConnectionState] = useState<boolean | null>(null);
    const handleCheckConnection = async () => {
        try {
            const res = await checkConnection({ variables: { id: integration.id } });
            const { connected, message } = res.data.checkSage100IntegrationConnection;
            setConnectionState(connected);
            publishToast({
                description: connected
                    ? t('sage100.checkConnectionSuccess')
                    : message || t('sage100.checkConnectionFailed'),
                status: connected ? 'success' : 'error',
            });
        } catch (e) {
            console.error(e);
            setConnectionState(false);
            publishToast({ description: t('sage100.checkConnectionError'), status: 'error' });
        }
    };

    const [updateOAuthFlowIntegration] = useMutation(TRIGGER_SAGE100_OAUTH_FLOW);
    const handleOAuthFlowIntegration = async (id) => {
        try {
            const res = await updateOAuthFlowIntegration({ variables: { id } });
            redirectTo(res.data.triggerSage100OauthFlow.redirectUrl);
        } catch (error) {
            console.error(error);
            publishToast({ description: t('sage100.oauthCallbackModal.error'), status: 'error' });
        }
    };

    const channels = integration ? [...integration.orderChannels, ...(integration.rfqChannels ?? [])] : [];

    return (
        <>
            <Table.Row>
                <Table.Cell>
                    <div className="flex flex-col gap-1.5 py-1">
                        <span className="font-medium text-content-strong">{integration.name}</span>
                        <div className="flex gap-2 items-center text-sm text-content-medium">
                            {integration.authState && (
                                <span>
                                    {t('sage100.authState', {
                                        authState: integration.authState,
                                    })}
                                </span>
                            )}

                            {/* Show OAuth login button for authorization_code flow or when authFlow is not set (backward compatibility) */}
                            {/* Hide for client_credentials flow (M2M doesn't need user login) */}
                            {(!integration.authFlow || integration.authFlow.toLowerCase() === 'authorization_code') && (
                                <RowControlIconButton
                                    onClick={() => handleOAuthFlowIntegration(integration.id)}
                                    className="text-content-brand-strong"
                                    title={t('sage100.triggerOAuth')}
                                >
                                    <LoginIcon />
                                </RowControlIconButton>
                            )}
                        </div>
                        {channels.length > 0 && (
                            <div className="flex gap-1.5 flex-wrap text-sm">
                                {channels.map((channel) => (
                                    <span
                                        key={channel.id}
                                        className="border-line-weak border border-solid bg-surface-strong flex gap-2 items-center px-1 rounded"
                                    >
                                        {channel.name}
                                    </span>
                                ))}
                            </div>
                        )}
                        <span className="text-xs text-content-medium">
                            {t('sage100.createdAt', {
                                date: new Date(integration.createdAt).toLocaleDateString(i18n.language, {
                                    dateStyle: 'medium',
                                }),
                            })}
                        </span>
                    </div>
                </Table.Cell>
                <Table.Cell className="align-top w-full">
                    <MasterDataStatus integration={integration} importProgress={importProgress} />
                </Table.Cell>
                <Table.Cell sticky="right">
                    <div className="flex gap-2 items-center">
                        <RowControlIconButton
                            onClick={handleCheckConnection}
                            disabled={checkingConnection}
                            title={t('sage100.checkConnection')}
                            className={classnames(
                                connectionState === true && 'text-content-success-strong',
                                connectionState === false && 'text-content-danger-strong'
                            )}
                        >
                            {checkingConnection ? <LoadingIcon spin /> : <ConnectionIcon />}
                        </RowControlIconButton>

                        <Tooltip
                            content={
                                loadedAt
                                    ? `${t('sage100.refreshStatus')} · ${t('sage100.statusFrom', {
                                          time: loadedAt.toLocaleTimeString(i18n.language, { timeStyle: 'short' }),
                                      })}`
                                    : t('sage100.refreshStatus')
                            }
                        >
                            <RowControlIconButton
                                onClick={onRefreshStatus}
                                disabled={refreshing}
                                aria-label={t('sage100.refreshStatus')}
                            >
                                <RefreshIcon spin={refreshing} />
                            </RowControlIconButton>
                        </Tooltip>

                        <RowControlIconButton
                            onClick={handleUpdateMasterDataIntegration}
                            title={t('sage100.triggerUpdate')}
                        >
                            <TriggerUpdateIcon />
                        </RowControlIconButton>

                        <RowControlIconButton asChild>
                            <Link
                                to={url(CUSTOMER_INTEGRATION_SAGE100_EDIT_PATH, {
                                    integrationId: integration.id,
                                })}
                                className="!text-content-strong hover:!text-content-strong"
                            >
                                <EditIcon />
                            </Link>
                        </RowControlIconButton>

                        <RowControlIconButton
                            className="hover:bg-surface-danger-weak text-content-danger-strong hover:text-content-danger-strong"
                            onClick={handleDeleteIntegration}
                        >
                            <DeleteIcon />
                        </RowControlIconButton>
                    </div>
                </Table.Cell>
            </Table.Row>
        </>
    );
};

export const Sage100OauthCallbackModal = () => {
    const { t } = useTranslation('integrations');
    const { publishToast } = useToaster();
    const { refetch } = useOutletContext<Sage100FormModalOutletContext>();

    const navigate = useNavigate();

    const [completeOauth] = useOauthCompleteMutation('sage100', INTEGRATIONS_SAGE100_OAUTH2_COMPLETE);
    const handleCompleteOauth = async () => {
        try {
            await completeOauth();
            await refetch();
            publishToast({ description: t('sage100.oauthCallbackModal.success'), status: 'success' });
            navigate(url(CUSTOMER_INTEGRATION_SAGE100_PATH));
        } catch (error) {
            console.error(error);
            publishToast({ description: t('sage100.oauthCallbackModal.error'), status: 'error' });
            navigate(url(CUSTOMER_INTEGRATION_SAGE100_PATH));
        }
    };

    useEffect(() => {
        void handleCompleteOauth();
    }, []);

    return (
        <Modal visible={true} noCloseButton>
            <div className="flex gap-2 items-center justify-center">
                <div>
                    <LoadingIcon spin />
                </div>
                <div>{t('sage100.oauthCallbackModal.description')}</div>
            </div>
        </Modal>
    );
};

const Sage100IntegrationPage = () => {
    const { t, i18n } = useTranslation('integrations');
    const documentationLink = i18n.language?.toLowerCase().startsWith('de')
        ? t('sage100.documentationLinkDe')
        : t('sage100.documentationLink');

    const {
        data: data,
        loading: loading,
        refetch: refetch,
    } = useQuery(GET_SAGE100_INTEGRATIONS, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
    });
    const integrations = data?.sage100Integrations || [];

    // Separate query so the page keeps working against a backend without import progress support
    const { data: progressData, refetch: refetchProgress } = useQuery(GET_SAGE100_IMPORT_PROGRESS, {
        fetchPolicy: 'no-cache',
        errorPolicy: 'ignore',
    });
    const importProgressById: Record<string, ImportProgress[]> = Object.fromEntries(
        (progressData?.sage100Integrations || []).map((entry) => [entry.id, entry.importProgress || []])
    );

    const [loadedAt, setLoadedAt] = useState<Date | null>(null);
    useEffect(() => {
        if (data) setLoadedAt(new Date());
    }, [data, progressData]);

    const refetchAll = async () => {
        await Promise.all([refetch(), refetchProgress().catch(() => undefined)]);
    };

    const backTooltipText = `${t('backTooltipText')} ${t('erp.pageTitle')}`;

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <IntegrationBreadcrumb
                        backTooltip={backTooltipText}
                        items={[
                            { label: t('erp.pageTitle'), to: url(CUSTOMER_INTEGRATION_ERP_PATH) },
                            { label: t('sage100.title') },
                        ]}
                    />

                    <div className="flex gap-2">
                        <Page.HeaderButton asChild>
                            <a href={documentationLink} target={'_blank'}>
                                <span className="flex gap-2 items-center">
                                    {t('sage100.documentation')}
                                    <ExternalLinkIcon />{' '}
                                </span>
                            </a>
                        </Page.HeaderButton>

                        <Page.HeaderButton asChild>
                            <Link to={url(CUSTOMER_INTEGRATION_SAGE100_ADD_PATH)}>
                                <AddIcon />
                                {t('sage100.addButton')}
                            </Link>
                        </Page.HeaderButton>
                    </div>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6 relative">
                    <Table scrollable className={classnames('w-full table-auto', loading && 'opacity-50')}>
                        <Table.Head>
                            <Table.Row>
                                <Table.HeadCell className="min-w-72 w-80">
                                    {t('sage100.table.integration')}
                                </Table.HeadCell>
                                <Table.HeadCell>{t('sage100.table.masterData')}</Table.HeadCell>
                                <Table.HeadCell sticky="right" className="min-w-14 w-14" />
                            </Table.Row>
                        </Table.Head>
                        <Table.Body>
                            {integrations && integrations.length > 0 ? (
                                integrations.map((integration) => (
                                    <Sage100Row
                                        key={integration.id}
                                        integration={integration}
                                        importProgress={importProgressById[integration.id] || []}
                                        refetch={refetchAll}
                                        onRefreshStatus={refetchAll}
                                        refreshing={loading}
                                        loadedAt={loadedAt}
                                    />
                                ))
                            ) : (
                                <Table.Row>
                                    <Table.Cell colSpan={3} className="text-center text-content-medium">
                                        {t('sage100.noIntegrations')}
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table>
                </Page.Content>
            </Page>

            <Outlet context={{ integrations, refetch: refetchAll }} />
        </Layout>
    );
};

export default Sage100IntegrationPage;
